import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, Check, ExternalLink } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { api } from "@/lib/api";

export default function PortfolioDetail() {
    const { slug } = useParams();
    const [project, setProject] = useState(null);
    const [missing, setMissing] = useState(false);

    useEffect(() => {
        setProject(null); setMissing(false);
        api.get(`/portfolio/${slug}`)
            .then((r) => setProject(r.data))
            .catch(() => setMissing(true));
    }, [slug]);

    if (missing) {
        return (
            <main className="pt-40 pb-24 text-center" data-testid="portfolio-not-found">
                <h1 className="font-display text-3xl font-bold text-white">Project not found</h1>
                <p className="mt-3 text-slate-400">This project may have been unpublished.</p>
                <Link to="/portfolio" className="mt-6 inline-flex items-center gap-2 text-violet-300 font-semibold" data-testid="portfolio-back-link">
                    <ArrowLeft className="h-4 w-4" /> Back to portfolio
                </Link>
            </main>
        );
    }
    if (!project) {
        return <main className="pt-40 pb-24"><div className="mx-auto max-w-5xl px-4"><div className="aspect-video rounded-2xl bg-hive-card animate-pulse" /></div></main>;
    }

    return (
        <main className="pt-32 sm:pt-40 pb-24">
            <Seo title={project.title} path={`/portfolio/${project.slug}`} description={project.description.slice(0, 155)} />
            <article className="mx-auto max-w-5xl px-4 sm:px-8" data-testid="portfolio-detail">
                <Link to="/portfolio" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-400 hover:text-white transition-colors" data-testid="portfolio-back-link">
                    <ArrowLeft className="h-4 w-4" /> All work
                </Link>
                <Reveal>
                    <div className="mt-6 flex flex-wrap items-center gap-3">
                        <span className="font-mono2 text-xs uppercase tracking-[0.25em] text-cyan-400">{project.category}</span>
                        {project.is_sample && (
                            <span className="rounded-full border border-amber-400/40 bg-amber-400/10 px-3 py-1 font-mono2 text-[10px] uppercase tracking-widest text-amber-300" data-testid="project-concept-badge">
                                Concept Project
                            </span>
                        )}
                    </div>
                    <h1 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight" data-testid="project-title">
                        {project.title}
                    </h1>
                </Reveal>
                <Reveal delay={0.1}>
                    <img src={project.cover_image} alt={`${project.title} cover image`} className="mt-10 w-full rounded-2xl border border-white/10 aspect-video object-cover" data-testid="project-cover-image" />
                </Reveal>
                {project.is_sample && (
                    <p className="mt-4 rounded-xl border border-amber-400/20 bg-amber-400/5 px-5 py-3 text-xs text-amber-200/80 leading-relaxed" data-testid="project-concept-disclaimer">
                        This is a demonstration concept created to showcase our approach. It is not a completed client engagement, and no client name or performance results are implied.
                    </p>
                )}
                <div className="mt-12 grid gap-10 md:grid-cols-3">
                    <Reveal className="md:col-span-2">
                        <Eyebrow>The Project</Eyebrow>
                        <p className="mt-4 text-base text-slate-300 leading-relaxed" data-testid="project-description">{project.description}</p>
                        {project.case_study?.length > 0 && (
                            <div className="mt-8" data-testid="project-case-study">
                                <h2 className="font-display text-xl font-bold text-white">The Story</h2>
                                {project.case_study.map((p, i) => (
                                    <p key={i} className="mt-4 text-base text-slate-400 leading-relaxed">{p}</p>
                                ))}
                            </div>
                        )}
                        {project.project_url && (
                            <a href={project.project_url} target="_blank" rel="noopener noreferrer" data-testid="project-url-link"
                                className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-cyan-300 hover:text-cyan-200">
                                Visit live project <ExternalLink className="h-4 w-4" />
                            </a>
                        )}
                        {project.gallery?.length > 0 && (
                            <div className="mt-10 grid gap-5">
                                {project.gallery.map((g, i) => (
                                    <img key={i} src={g} alt={`${project.title} gallery image ${i + 1}`} loading="lazy" className="rounded-2xl border border-white/10 w-full object-cover" />
                                ))}
                            </div>
                        )}
                    </Reveal>
                    <div className="space-y-6">
                        {project.objectives?.length > 0 && (
                            <Reveal delay={0.08}>
                                <div className="rounded-2xl border border-white/10 bg-hive-card p-6">
                                    <h2 className="font-display font-bold text-white">Objectives</h2>
                                    <ul className="mt-3 space-y-2">
                                        {project.objectives.map((o) => (
                                            <li key={o} className="flex items-start gap-2 text-sm text-slate-300">
                                                <Check className="mt-0.5 h-4 w-4 shrink-0 text-violet-400" /> {o}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </Reveal>
                        )}
                        {project.deliverables?.length > 0 && (
                            <Reveal delay={0.14}>
                                <div className="rounded-2xl border border-white/10 bg-hive-card p-6">
                                    <h2 className="font-display font-bold text-white">Deliverables</h2>
                                    <ul className="mt-3 space-y-2">
                                        {project.deliverables.map((d) => (
                                            <li key={d} className="flex items-start gap-2 text-sm text-slate-300">
                                                <Check className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" /> {d}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </Reveal>
                        )}
                    </div>
                </div>
                <Reveal>
                    <div className="mt-16 rounded-2xl border border-violet-500/25 bg-hive-card p-10 text-center">
                        <h2 className="font-display text-2xl sm:text-3xl font-bold text-white">Want results like this?</h2>
                        <p className="mx-auto mt-3 max-w-lg text-sm text-slate-400">Discuss a similar project with our team — every inquiry gets a tracked reference number.</p>
                        <Link to={`/contact?service=${encodeURIComponent(project.category === "Social Media" ? "Social Media Management" : project.category)}`}
                            data-testid="project-cta-button"
                            className="mt-7 inline-flex items-center gap-2 rounded-full bg-violet-600 px-8 py-3.5 font-semibold text-white transition-all hover:bg-violet-500 hover:shadow-[0_0_32px_rgba(124,58,237,0.5)]">
                            Discuss a Similar Project <ArrowUpRight className="h-4 w-4" />
                        </Link>
                    </div>
                </Reveal>
            </article>
        </main>
    );
}
